/**
 * Mobility MVP: Mobility Profile Tab Component
 * Displays mobility assessment summary, retake actions, and educational content
 * Follows FIGMA Profile Tabs layout
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useMobilityProfile } from '@/hooks/useMobility';
import { MobilityQuiz } from './MobilityQuiz';
import { PelvicTiltTest } from './PelvicTiltTest';
import { 
  Activity, 
  RotateCcw, 
  Info,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronRight } from 'lucide-react';

const MOBILITY_LABELS: Record<string, { label: string; icon: React.ReactNode }> = {
  posterior_chain: { label: 'Posterior Chain', icon: <Activity className="w-4 h-4" /> },
  thoracic_rotation: { label: 'Thoracic Rotation', icon: <Activity className="w-4 h-4" /> },
  hip_mobility: { label: 'Hip Mobility', icon: <Activity className="w-4 h-4" /> },
  shoulder_mobility: { label: 'Shoulder Mobility', icon: <Activity className="w-4 h-4" /> },
  pelvic_control: { label: 'Pelvic Control', icon: <Activity className="w-4 h-4" /> }
};

const CLASSIFICATION_LABELS: Record<string, string> = {
  good: 'Good',
  limited: 'Limited',
  good_control: 'Good Control',
  limited_control: 'Limited Control',
  no_control: 'No Control'
};

export function MobilityProfileTab() {
  const { data: profileData, isLoading } = useMobilityProfile();
  const [showQuiz, setShowQuiz] = useState(false);
  const [showPelvicTilt, setShowPelvicTilt] = useState(false);
  const [learnExpanded, setLearnExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const profile = profileData?.profile;
  const hasProfile = profileData?.has_profile;
  const lastPelvicTilt = profileData?.pelvic_tilt_last_run;

  return (
    <div className="space-y-6">
      {/* Mobility Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>Mobility Summary</CardTitle>
          <CardDescription>
            Your current mobility assessment results
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasProfile ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No mobility assessment completed yet.</p>
              <p className="text-sm mt-2">Complete the assessment to see your mobility profile.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {profile?.posterior_chain && (
                <div className="flex items-center justify-between py-2 border-b">
                  <div className="flex items-center gap-2">
                    {MOBILITY_LABELS.posterior_chain.icon}
                    <span className="text-sm font-medium">{MOBILITY_LABELS.posterior_chain.label}</span>
                  </div>
                  <Badge variant={profile.posterior_chain === 'good' ? 'default' : 'secondary'}>
                    {CLASSIFICATION_LABELS[profile.posterior_chain] || profile.posterior_chain}
                  </Badge>
                </div>
              )}
              
              {profile?.thoracic_rotation && (
                <div className="flex items-center justify-between py-2 border-b">
                  <div className="flex items-center gap-2">
                    {MOBILITY_LABELS.thoracic_rotation.icon}
                    <span className="text-sm font-medium">{MOBILITY_LABELS.thoracic_rotation.label}</span>
                  </div>
                  <Badge variant={profile.thoracic_rotation === 'good' ? 'default' : 'secondary'}>
                    {CLASSIFICATION_LABELS[profile.thoracic_rotation] || profile.thoracic_rotation}
                  </Badge>
                </div>
              )}
              
              {profile?.hip_mobility && (
                <div className="flex items-center justify-between py-2 border-b">
                  <div className="flex items-center gap-2">
                    {MOBILITY_LABELS.hip_mobility.icon}
                    <span className="text-sm font-medium">{MOBILITY_LABELS.hip_mobility.label}</span>
                  </div>
                  <Badge variant={profile.hip_mobility === 'good' ? 'default' : 'secondary'}>
                    {CLASSIFICATION_LABELS[profile.hip_mobility] || profile.hip_mobility}
                  </Badge>
                </div>
              )}
              
              {profile?.shoulder_mobility && (
                <div className="flex items-center justify-between py-2 border-b">
                  <div className="flex items-center gap-2">
                    {MOBILITY_LABELS.shoulder_mobility.icon}
                    <span className="text-sm font-medium">{MOBILITY_LABELS.shoulder_mobility.label}</span>
                  </div>
                  <Badge variant={profile.shoulder_mobility === 'good' ? 'default' : 'secondary'}>
                    {CLASSIFICATION_LABELS[profile.shoulder_mobility] || profile.shoulder_mobility}
                  </Badge>
                </div>
              )}
              
              {profile?.pelvic_control && (
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    {MOBILITY_LABELS.pelvic_control.icon}
                    <span className="text-sm font-medium">{MOBILITY_LABELS.pelvic_control.label}</span>
                  </div>
                  <Badge variant={
                    profile.pelvic_control === 'good_control' ? 'default' : 
                    profile.pelvic_control === 'limited_control' ? 'secondary' : 
                    'destructive'
                  }>
                    {CLASSIFICATION_LABELS[profile.pelvic_control] || profile.pelvic_control}
                  </Badge>
                </div>
              )}

              {lastPelvicTilt && (
                <div className="pt-2 border-t">
                  <p className="text-sm font-medium">Last pelvic tilt test</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {lastPelvicTilt.created_at ? `Date: ${new Date(lastPelvicTilt.created_at).toLocaleDateString()}` : null}
                    {typeof lastPelvicTilt.confidence === 'number' ? ` • Confidence: ${Math.round(lastPelvicTilt.confidence * 100)}%` : null}
                  </p>
                  {Array.isArray(lastPelvicTilt.evidence) && lastPelvicTilt.evidence.length > 0 && (
                    <ul className="mt-2 text-xs text-muted-foreground list-disc pl-5 space-y-1">
                      {lastPelvicTilt.evidence.slice(0, 3).map((e, idx) => (
                        <li key={idx}>{e}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {profile?.injury_flags && profile.injury_flags.length > 0 && (
                <div className="pt-2">
                  <p className="text-sm font-medium mb-2">Injury History:</p>
                  <div className="flex flex-wrap gap-2">
                    {profile.injury_flags.map((injury) => (
                      <Badge key={injury} variant="outline">{injury}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {profileData?.last_updated && (
                <p className="text-xs text-muted-foreground pt-2">
                  Last updated: {new Date(profileData.last_updated).toLocaleDateString()}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions Card */}
      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button 
            onClick={() => setShowQuiz(true)} 
            variant="outline" 
            className="w-full justify-start"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Retake Questionnaire
          </Button>
          <Button 
            onClick={() => setShowPelvicTilt(true)} 
            variant="outline" 
            className="w-full justify-start"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Retake Pelvic Tilt Test
          </Button>
        </CardContent>
      </Card>

      {/* Educational Card */}
      <Card>
        <Collapsible open={learnExpanded} onOpenChange={setLearnExpanded}>
          <CollapsibleTrigger className="w-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-muted-foreground" />
                  <CardTitle>How mobility influences your swing</CardTitle>
                </div>
                <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform ${learnExpanded ? 'rotate-90' : ''}`} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  • <strong>Limited mobility</strong> can cause compensations in your swing, leading to inconsistent ball striking and potential injury.
                </p>
                <p>
                  • <strong>Pelvic control</strong> is essential for maintaining posture and generating power through rotation.
                </p>
                <p>
                  • Understanding your mobility limitations helps our AI identify <strong>root causes</strong> of swing faults rather than just symptoms.
                </p>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Quiz Dialog - Full screen overlay */}
      <Dialog open={showQuiz} onOpenChange={setShowQuiz}>
        <DialogContent className="!inset-0 !max-w-none !w-screen !h-screen !p-0 !m-0 !translate-x-0 !translate-y-0 !rounded-none sm:!inset-0 sm:!max-w-none sm:!w-screen sm:!h-screen sm:!translate-x-0 sm:!translate-y-0 sm:!rounded-none">
          <DialogTitle className="sr-only">Mobility Questionnaire</DialogTitle>
          <DialogDescription className="sr-only">Complete the mobility assessment questionnaire</DialogDescription>
          <MobilityQuiz 
            onComplete={() => setShowQuiz(false)} 
            onSkip={() => setShowQuiz(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Pelvic Tilt Dialog - Full screen overlay */}
      <Dialog open={showPelvicTilt} onOpenChange={setShowPelvicTilt}>
        <DialogContent 
          className="!inset-0 !max-w-none !w-screen !h-screen !p-0 !m-0 !translate-x-0 !translate-y-0 !rounded-none sm:!inset-0 sm:!max-w-none sm:!w-screen sm:!h-screen sm:!translate-x-0 sm:!translate-y-0 sm:!rounded-none !overflow-hidden"
          onOpenAutoFocus={(e) => {
            // Prevent auto-focus from triggering clicks
            e.preventDefault();
          }}
        >
          <DialogTitle className="sr-only">Pelvic Tilt Test</DialogTitle>
          <DialogDescription className="sr-only">Record a video of your pelvic tilt movement</DialogDescription>
          <div className="h-full w-full overflow-auto">
            <PelvicTiltTest 
              key={showPelvicTilt ? 'pelvic-tilt-test' : undefined}
              onComplete={() => setShowPelvicTilt(false)} 
              onSkip={() => setShowPelvicTilt(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
